/**
 * Team Design — team-name overlay (render layer).
 *
 * Owns:
 *  - DOM lookup helpers for the product card / preview tree (exposed on NS.dom)
 *  - injectTeamText() — replaces the `<g id="teamText">`, `<g id="numberText">`
 *    and `<g id="nameText">` placeholders inside the pattern SVG with recolored,
 *    shrink-to-fit `<text>` nodes anchored to the matching `#team`, `#number`,
 *    `#name` rects (no-ops per slot when the SVG omits it)
 *  - refreshAllVisiblePreviews() — re-runs injection on every visible card
 *  - One-time restore of persisted team-name + logo state from
 *    window.TeamDesignStorage so auto-mode activates on page load before any
 *    other team-design script runs
 *
 * Exposes on window.CollectionTeamText:
 *  - state    shared { currentText, autoMode, visibleCards, currentLogoUrl }
 *  - dom      lookup helpers
 *  - inject   { INJECTED_ID, injectTeamText, refreshAllVisiblePreviews }
 *
 * SVG fetch + colorize live in window.TeamDesignSvg.
 *
 * Must load AFTER team-design.svg-engine.js and team-design.storage.js,
 * and BEFORE team-design.text-auto.js / team-design.text.js.
 */
(function () {
  'use strict';

  var NS    = (window.CollectionTeamText = window.CollectionTeamText || {});
  var state = (NS.state = NS.state || {
    currentText:  '',
    autoMode:     false,
    visibleCards: new Set(),
  });

  // Restore persisted state synchronously so the input + upload UI reflect
  // the cookie on page load. We deliberately leave `state.armed` false here
  // — even though `currentText` / `currentLogoUrl` are populated, the auto-
  // render gates won't fire until arm() is called by an explicit shopper
  // interaction (focus on the team input or logo upload, color swatch
  // click, font click). Cookie restoration alone shouldn't trigger an SVG
  // swap; otherwise every collection-page refresh with a saved team name
  // pre-loads the customized preview before the shopper expresses any
  // intent on THIS visit.
  (function () {
    var storage = window.TeamDesignStorage;
    if (!storage) return;
    var saved = storage.get();
    if (saved.teamName) {
      state.currentText = saved.teamName;
      state.autoMode    = true;
    }
    if (saved.logo) {
      state.currentLogoUrl = (saved.logo.urls && (saved.logo.urls.medium || saved.logo.urls.original)) || null;
    }
  })();

  // Auto-load gate. Stays `false` until the shopper:
  //   - focuses the team-name input
  //   - focuses or clicks the logo upload
  //   - picks a color swatch
  //   - picks a font
  // Once flipped to `true`, auto.render / logo.applyLogoToCard are allowed
  // to paint visible cards. One-way ratchet — there's no "un-arm" path,
  // since any of the four actions above is an unambiguous intent signal.
  state.armed = false;

  // Public arm() — call from the four interaction sites above. Idempotent:
  // re-entry after the first call short-circuits before any work. Promotes
  // the API default font (no-op if the picker already has one selected)
  // and force-paints currently-visible cards so the shopper sees an
  // immediate response to their interaction.
  window.CollectionTeamText.arm = function () {
    if (state.armed) return;
    state.armed = true;

    var qc = window.CollectionQuickCustomization;
    if (qc && qc.selectDefaultFont) qc.selectDefaultFont();

    var ns = window.CollectionTeamText;
    if (ns.auto && ns.auto.renderVisible)        ns.auto.renderVisible();
    if (ns.logo && ns.logo.applyToVisibleCards)  ns.logo.applyToVisibleCards();
  };

  var INJECTED_ID        = 'injectedTeamText';
  var INJECTED_NUMBER_ID = 'injectedNumberText';
  var INJECTED_NAME_ID   = 'injectedNameText';

  // ─── DOM helpers ──────────────────────────────────────────────────────────
  function getPreview(card)   { return card.querySelector('.card-design-preview'); }
  function getMediaWrap(card) { return card.querySelector('.card-product-figma__image-frame .media--transparent'); }
  function getFirstSwatch(c)  { return c.querySelector('.card-color-swatch'); }
  function findSvg(previewEl) { return previewEl && previewEl.querySelector ? previewEl.querySelector('svg') : null; }

  // ─── SVG transform helpers ────────────────────────────────────────────────
  // Tiny SVG-matrix algebra used by readRect() so the rect's box is resolved
  // in SVG-root coordinates without leaning on rect.getCTM() (which we've seen
  // return identity for elements that haven't been laid out yet, dropping the
  // injected text into the top-left of the SVG).
  function multiplyMatrix(A, B) {
    return {
      a: A.a * B.a + A.c * B.b,
      b: A.b * B.a + A.d * B.b,
      c: A.a * B.c + A.c * B.d,
      d: A.b * B.c + A.d * B.d,
      e: A.a * B.e + A.c * B.f + A.e,
      f: A.b * B.e + A.d * B.f + A.f,
    };
  }
  function identityMatrix() { return { a: 1, b: 0, c: 0, d: 1, e: 0, f: 0 }; }
  function isIdentity(m)    { return m.a === 1 && m.b === 0 && m.c === 0 && m.d === 1 && m.e === 0 && m.f === 0; }

  // Parse an SVG `transform` attribute value (e.g. "matrix(...) translate(...)").
  // Handles matrix/translate/scale/rotate — the four primitives the source
  // SVGs use. Returns identity for empty/unknown input.
  function parseTransformAttr(s) {
    var result = identityMatrix();
    if (!s) return result;
    var re = /(matrix|translate|scale|rotate)\s*\(([^)]*)\)/g;
    var match;
    while ((match = re.exec(s)) !== null) {
      var fn   = match[1];
      var args = match[2].split(/[\s,]+/).map(parseFloat).filter(function (v) { return !isNaN(v); });
      var m = null;
      if (fn === 'matrix' && args.length === 6) {
        m = { a: args[0], b: args[1], c: args[2], d: args[3], e: args[4], f: args[5] };
      } else if (fn === 'translate' && args.length >= 1) {
        m = { a: 1, b: 0, c: 0, d: 1, e: args[0], f: args[1] || 0 };
      } else if (fn === 'scale' && args.length >= 1) {
        var sx = args[0], sy = args.length >= 2 ? args[1] : args[0];
        m = { a: sx, b: 0, c: 0, d: sy, e: 0, f: 0 };
      } else if (fn === 'rotate' && args.length >= 1) {
        var rad = args[0] * Math.PI / 180;
        var co  = Math.cos(rad), si = Math.sin(rad);
        if (args.length === 3) {
          var cx = args[1], cy = args[2];
          m = { a: co, b: si, c: -si, d: co, e: cx - co * cx + si * cy, f: cy - si * cx - co * cy };
        } else {
          m = { a: co, b: si, c: -si, d: co, e: 0, f: 0 };
        }
      }
      if (m) result = multiplyMatrix(result, m);
    }
    return result;
  }

  // Walk from `el` up to (but not including) `svg`, composing each ancestor's
  // `transform` attribute. The rect's own transform applies first (innermost),
  // then each parent, so we pre-multiply as we walk up.
  function getElementToSvgMatrix(el, svg) {
    var combined = identityMatrix();
    var node     = el;
    while (node && node !== svg) {
      if (node.getAttribute) {
        var attr = node.getAttribute('transform');
        if (attr) {
          var nodeM = parseTransformAttr(attr);
          combined  = multiplyMatrix(nodeM, combined);
        }
      }
      node = node.parentNode;
    }
    return combined;
  }

  // ─── SVG rect + theme readers ────────────────────────────────────────────
  // Resolve the rect's bbox in SVG-root coordinates so the injected <text>
  // (appended at the SVG root) lines up regardless of how the source SVG
  // positions the rect. Two pattern styles are seen in production:
  //   - Older patterns: rect carries explicit x/y/width/height and no
  //     transforms — fast path, returns the attribute values directly.
  //   - Newer #number slot: rect omits x/y and places itself via
  //     `transform="matrix(-1 0 0 1 419.082 285.488)"` inside `g#numberText`.
  //     We default x/y to 0 and apply the accumulated rect→svg transform.
  function readRect(svg, selector) {
    var rect = svg.querySelector(selector);
    if (!rect) return null;

    var w = parseFloat(rect.getAttribute('width'));
    var h = parseFloat(rect.getAttribute('height'));
    if (isNaN(w) || isNaN(h) || w <= 0 || h <= 0) return null;

    var x = parseFloat(rect.getAttribute('x'));
    var y = parseFloat(rect.getAttribute('y'));
    if (isNaN(x)) x = 0;
    if (isNaN(y)) y = 0;

    var matrix = getElementToSvgMatrix(rect, svg);
    if (isIdentity(matrix)) return { x: x, y: y, w: w, h: h };

    var corners = [
      { x: x,     y: y     },
      { x: x + w, y: y     },
      { x: x,     y: y + h },
      { x: x + w, y: y + h },
    ];
    var minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (var i = 0; i < corners.length; i++) {
      var px = matrix.a * corners[i].x + matrix.c * corners[i].y + matrix.e;
      var py = matrix.b * corners[i].x + matrix.d * corners[i].y + matrix.f;
      if (px < minX) minX = px;
      if (py < minY) minY = py;
      if (px > maxX) maxX = px;
      if (py > maxY) maxY = py;
    }
    return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
  }

  function readActiveTheme(previewEl) {
    // Banner picker is the source of truth — every card recolors with the
    // same theme when the shopper clicks a swatch in the side panel, so
    // the team-name overlay must mirror the same theme. state.bannerTheme
    // is set by team-design.banner-swatches.js (on click + on the API
    // default-theme seed at page load), so this branch hits in both the
    // user-clicked and the never-clicked states.
    var banner = state.bannerTheme;
    if (banner) {
      return {
        primary: banner.primaryColor || '#000000',
        accent:  banner.accentColor  || '#ffffff',
      };
    }
    // Per-card swatch fallback (banner config absent on this page, e.g.
    // collection has no `custom.quick_customization_code` metafield).
    var card = previewEl.closest ? previewEl.closest('.card-wrapper') : null;
    if (!card) return { primary: '#000000', accent: '#ffffff' };
    var swatch = card.querySelector('.card-color-swatch--active') ||
                 card.querySelector('.card-color-swatch');
    if (!swatch) return { primary: '#000000', accent: '#ffffff' };
    return {
      primary: swatch.dataset.primary || '#000000',
      accent:  swatch.dataset.accent  || '#ffffff',
    };
  }

  // ─── Placeholder text ─────────────────────────────────────────────────────
  // Shown when the corresponding input is blank (team name) or always, for
  // now, in the case of number / player name where no input field exists
  // yet. We render these with our own styled <text> (same font/theme as a
  // real value) instead of leaving the SVG's shipped <g id="...Text">
  // placeholders in place — those groups' fonts are baked into the SVG and
  // don't react to the banner picker, which feels broken when previewing
  // fonts.
  var EMPTY_TEXT_PLACEHOLDER   = 'TEAM NAME';
  var EMPTY_NUMBER_PLACEHOLDER = '10';
  var EMPTY_NAME_PLACEHOLDER   = 'PLAYER NAME';

  // BASEBALL collections use a mixed-case "Teamname" placeholder instead of
  // the default all-caps "TEAM NAME" — read the collection's quick-
  // customization code off the sidebar (set from the metafield in
  // snippets/quick-customization.liquid) and switch on it.
  function getEmptyTextPlaceholder() {
    var sidebar = document.querySelector('[class^="qc-sidebar-"], [class*=" qc-sidebar-"]');
    var code    = sidebar ? (sidebar.getAttribute('data-collection-code') || '') : '';
    if (code.toUpperCase() === 'BASEBALL') return 'Teamname';
    return EMPTY_TEXT_PLACEHOLDER;
  }

  // Picks the right banner font for this slot. The quick-customization
  // picker stores THREE fonts on state — #team renders in textFont,
  // #name in playerNameFont, and the jersey #number in numberFont. The
  // player slot falls back to the text font when an older picker bundle
  // only populated the two-font pair. We fall back through the family
  // chain (Anton → Barlow Semi Condensed → Barlow → sans-serif) when the
  // shopper hasn't picked a pair yet so existing cards keep their look.
  function fontFamilyForSlot(slotFontKey) {
    var banner = state[slotFontKey];
    if (slotFontKey === 'bannerPlayerNameFont' && !(banner && banner.family)) {
      banner = state.bannerTextFont;
    }
    var bannerFamily = banner && banner.family;
    return bannerFamily
      ? "'" + bannerFamily + "', 'Anton', 'Barlow Semi Condensed', 'Barlow', sans-serif"
      : "'Anton', 'Barlow Semi Condensed', 'Barlow', sans-serif";
  }

  // Render one styled <text> overlay anchored to `rectSelector`. If neither
  // the rect nor the shipped <g> group is present in the SVG, this no-ops
  // (some patterns don't carry every slot).
  function injectOverlay(svg, theme, opts) {
    var prior = svg.querySelector('#' + opts.injectedId);
    if (prior && prior.parentNode) prior.parentNode.removeChild(prior);

    // Read the anchor rect BEFORE removing the shipped group — some patterns
    // (e.g. the number slot in newer pattern sets) nest `rect#number` inside
    // `g#numberText`, so removing the group first would also remove the rect
    // and leave nothing to anchor the injected <text> to.
    var rect = readRect(svg, opts.rectSelector);

    // Always remove the SVG's shipped placeholder group. We render our own
    // <text> in every case (using a placeholder when there's no input), so
    // there's no reason to snapshot and later restore the original group.
    var originalGroup = svg.querySelector(opts.groupSelector);
    if (originalGroup && originalGroup.parentNode) {
      originalGroup.parentNode.removeChild(originalGroup);
    }

    if (!rect) return;

    var cx = rect.x + rect.w / 2;
    var cy = rect.y + rect.h / 2;

    // The team-name input can wrap to a second line on Enter. Both 1- and
    // 2-line cases use the same startSize tuned for a single bold line —
    // the rect was drawn for that, and forcing two lines to share its
    // height makes the text unreadable. Multi-line is allowed to overflow
    // the rect vertically (the rect is a horizontal centering guide, not a
    // hard boundary); only width is constrained in shrink-to-fit below.
    //
    // Line 1 stays anchored where a single line would sit; line 2 drops a
    // full em below it. Anton's cap-height (~0.78em) plus descenders need
    // at least ~1em of baseline-to-baseline space — tighter values overlap
    // the strokes.
    //
    // `startScale` is per-slot: team uses 1.25 (overflow-by-design), while
    // number/name use ~0.9 so the placeholder visually sits INSIDE its rect
    // — the 0.24× stroke adds substantial outline outside the cap height,
    // so 1.0× would push past the placeholder's drawn box.
    var lines      = opts.displayText.split('\n');
    var lineCount  = lines.length;
    var lineGap    = 1.0; // baseline-to-baseline in em
    var startScale = typeof opts.startScale === 'number' ? opts.startScale : 1.25;
    // `sizeMultiplier` from the font's `fontConfig` scales the auto-fit
    // starting size. Shrink-to-fit still constrains to `rect.w`, so a big
    // multiplier on a narrow slot just triggers earlier down-scaling —
    // multipliers only visibly kick in when the text is well under the
    // rect's width at the base scale (e.g. decorative script fonts that
    // "read small" and need a boost).
    var sizeMultiplier = typeof opts.sizeMultiplier === 'number' && opts.sizeMultiplier > 0
      ? opts.sizeMultiplier
      : 1;
    var startSize  = rect.h * startScale * sizeMultiplier;

    // Safari has long-standing bugs with SVG `paint-order` on dynamically-
    // injected <text> nodes: neither the presentation attribute nor the CSS
    // property is reliably honored across desktop / iOS / WebView versions,
    // so the thick stroke ends up painted on top of the fill and eats the
    // letter shapes. Instead of a single <text> with stroke+fill, we emit
    // two <text> siblings inside a <g id="…"> wrapper: a stroke-only text
    // first, then a fill-only text on top. SVG paints in document order, so
    // this reproduces `paint-order: stroke fill` in a way every renderer
    // agrees on — the same approach preview3d uses on canvas (strokeText()
    // before fillText()).
    var ns    = 'http://www.w3.org/2000/svg';
    var group = document.createElementNS(ns, 'g');
    group.setAttribute('id', opts.injectedId);

    var fontFamily   = fontFamilyForSlot(opts.fontStateKey);
    var strokeWidth  = Math.max(1.9, startSize * 0.24);
    var MULTI_LINE_LIFT_EM = 0.25;
    var firstDy = lineCount > 1 ? -MULTI_LINE_LIFT_EM + 'em' : '0';

    // Build a <text> with shared geometry — the two layers differ only in
    // fill / stroke, so this factory keeps them byte-identical everywhere
    // else and guarantees perfect alignment.
    function buildLayer(fillValue, strokeValue, strokeWidthValue) {
      var el = document.createElementNS(ns, 'text');
      el.setAttribute('x',                 cx);
      el.setAttribute('y',                 cy);
      el.setAttribute('font-size',         startSize);
      el.setAttribute('font-weight',       '500');
      // Each slot picks its own banner font: #team reads
      // state.bannerTextFont, #name reads state.bannerPlayerNameFont, and
      // the number slot reads state.bannerNumberFont. opts.fontStateKey is
      // one of those names — the caller picks the right one per slot, the
      // fallback chain inside fontFamilyForSlot handles the "no pair
      // picked yet" case.
      el.setAttribute('font-family',       fontFamily);
      el.setAttribute('text-anchor',       'middle');
      el.setAttribute('dominant-baseline', 'central');
      el.setAttribute('fill',              fillValue);
      el.setAttribute('stroke',            strokeValue);
      if (strokeValue !== 'none') {
        el.setAttribute('stroke-width',    strokeWidthValue);
        el.setAttribute('stroke-linejoin', 'round');
        el.setAttribute('stroke-linecap',  'round');
      }
      // Render each line as a tspan that resets x to cx (so text-anchor="middle"
      // centers each row independently). Each subsequent line drops `lineGap`
      // em below the previous baseline. Multi-line gets a small upward nudge
      // (MULTI_LINE_LIFT_EM) on line 1 so the block doesn't visually sag below
      // the band — it's well short of full centering (which would be -0.5em)
      // since we still want line 1 to mostly sit where a single line would
      // render.
      for (var li = 0; li < lineCount; li++) {
        var tspan = document.createElementNS(ns, 'tspan');
        tspan.setAttribute('x',  cx);
        tspan.setAttribute('dy', li === 0 ? firstDy : lineGap + 'em');
        tspan.textContent = lines[li];
        el.appendChild(tspan);
      }
      return el;
    }

    var strokeText = buildLayer('none', theme.primary, strokeWidth);
    var fillText   = buildLayer(theme.accent, 'none', null);
    group.appendChild(strokeText);
    group.appendChild(fillText);

    // Paint behind the shadow overlay so it sits under the jersey shading.
    // SVG paint order = document order; earlier siblings render first.
    // Some source SVGs have a trailing space in the overlay's id/class
    // (e.g. `id="overlayShadow "`), so #overlayShadow alone misses them —
    // try the trailing-space variant and the class selector as fallbacks.
    var overlayShadow = svg.querySelector('#overlayShadow') ||
                        svg.querySelector('[id^="overlayShadow"]') ||
                        svg.querySelector('.overlayShadow');
    if (overlayShadow && overlayShadow.parentNode) {
      overlayShadow.parentNode.insertBefore(group, overlayShadow);
    } else {
      svg.appendChild(group);
    }

    // Shrink-to-fit: scale so the widest line fits rect.w. Height is
    // intentionally NOT constrained for multi-line — see startSize comment.
    // Measure on fillText (both layers have identical geometry) and apply
    // the new font-size to both layers plus stroke-width to the stroke layer.
    try {
      var maxWidth = 0;
      var tspans   = fillText.getElementsByTagName('tspan');
      for (var ti = 0; ti < tspans.length; ti++) {
        var w = tspans[ti].getComputedTextLength();
        if (w > maxWidth) maxWidth = w;
      }
      if (maxWidth > 0 && maxWidth > rect.w) {
        var fontSize = startSize * (rect.w / maxWidth);
        strokeText.setAttribute('font-size',    fontSize);
        strokeText.setAttribute('stroke-width', Math.max(1.9, fontSize * 0.24));
        fillText.setAttribute('font-size',      fontSize);
      }
    } catch (e) {
      // getComputedTextLength throws if the element isn't in a rendered tree;
      // keep the start size in that case.
    }
  }

  function injectTeamText(previewEl) {
    var svg = findSvg(previewEl);
    if (!svg) return;

    var theme = readActiveTheme(previewEl);

    // Rendering options now live on each font's `fontConfig` (set in the
    // dashboard's Add Font modal). Casing + suffix apply per letter slot —
    // #team from the text font's config, #name from the player font's —
    // while the #number slot is rendered with the
    // number font and left untouched. Suffix is appended after the
    // displayed text (e.g. shopper types "BLUE JAYS", text-font's suffix
    // is "." → renders "BLUE JAYS.").
    var textFont       = state.bannerTextFont   || null;
    var numberFont     = state.bannerNumberFont || null;
    // Player-name slot font — falls back to the text side when an older
    // picker bundle only populated the two-font pair, so #name keeps its
    // historical text-font look in that case.
    var playerFont     = state.bannerPlayerNameFont || textFont;
    var textCfg        = (textFont   && textFont.fontConfig)   || {};
    var numberCfg      = (numberFont && numberFont.fontConfig) || {};
    var playerCfg      = (playerFont && playerFont.fontConfig) || {};
    var enforceAllCaps = !!textCfg.allCaps;
    var textSuffix     = textCfg.suffix || '';
    var playerAllCaps  = !!playerCfg.allCaps;
    var playerSuffix   = playerCfg.suffix || '';
    var textScale      = typeof textCfg.sizeMultiplier   === 'number' ? textCfg.sizeMultiplier   : 1;
    var numberScale    = typeof numberCfg.sizeMultiplier === 'number' ? numberCfg.sizeMultiplier : 1;
    var playerScale    = typeof playerCfg.sizeMultiplier === 'number' ? playerCfg.sizeMultiplier : 1;
    function applyTextOptions(value) {
      var out = value;
      if (enforceAllCaps) out = out.toUpperCase();
      if (textSuffix)     out = out + textSuffix;
      return out;
    }
    function applyPlayerOptions(value) {
      var out = value;
      if (playerAllCaps) out = out.toUpperCase();
      if (playerSuffix)  out = out + playerSuffix;
      return out;
    }

    injectOverlay(svg, theme, {
      rectSelector:  '#team',
      groupSelector: '#teamText',
      injectedId:    INJECTED_ID,
      displayText:   applyTextOptions(state.currentText || getEmptyTextPlaceholder()),
      fontStateKey:  'bannerTextFont',
      sizeMultiplier: textScale,
    });

    // Number + name don't have input fields yet, so we always render the
    // placeholder. injectOverlay no-ops if the SVG doesn't carry the matching
    // rect/group (some patterns omit one or both slots). The number slot
    // uses the pair's number-side font; #name uses the pair's dedicated
    // player-name font (falling back to the text side for older pickers).
    // startScale is dialled down from team's
    // 1.25 so the placeholder fits inside its rect (team intentionally
    // overflows; number/name shouldn't).
    injectOverlay(svg, theme, {
      rectSelector:  '#number',
      groupSelector: '#numberText',
      injectedId:    INJECTED_NUMBER_ID,
      displayText:   EMPTY_NUMBER_PLACEHOLDER,
      startScale:    0.9,
      fontStateKey:  'bannerNumberFont',
      sizeMultiplier: numberScale,
    });

    injectOverlay(svg, theme, {
      rectSelector:  '#name',
      groupSelector: '#nameText',
      injectedId:    INJECTED_NAME_ID,
      displayText:   applyPlayerOptions(EMPTY_NAME_PLACEHOLDER),
      startScale:    0.9,
      fontStateKey:  'bannerPlayerNameFont',
      sizeMultiplier: playerScale,
    });
  }

  function refreshAllVisiblePreviews() {
    var previews = document.querySelectorAll('.card-design-preview');
    for (var i = 0; i < previews.length; i++) {
      var el = previews[i];
      if (el.style.display !== 'none' && el.firstChild) {
        injectTeamText(el);
      }
    }
  }

  // ─── Public API ───────────────────────────────────────────────────────────
  NS.dom = {
    getPreview:     getPreview,
    getMediaWrap:   getMediaWrap,
    getFirstSwatch: getFirstSwatch,
    findSvg:        findSvg,
  };
  NS.inject = {
    INJECTED_ID:               INJECTED_ID,
    injectTeamText:            injectTeamText,
    refreshAllVisiblePreviews: refreshAllVisiblePreviews,
  };
})();